import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, AppBar, Toolbar, Typography, IconButton, Button, Paper,
    BottomNavigation, BottomNavigationAction, CircularProgress,
    Card, CardContent, Chip, Avatar,
    Divider, MenuItem, Alert,
    Drawer, TextField, InputAdornment, ListItemButton, ListItemText,
    Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import BusinessLogo, { getInitials } from '../common/BusinessLogo';
import {
    StorefrontOutlined as ShopIcon,
    BusinessOutlined as VendorIcon,
    Home as HomeIcon,
    AccountCircle,
    ArrowBack as ArrowBackIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    ExitToApp,
    ChevronRight as ChevronRightIcon,
    Payment as PaymentIcon,
    Phone as PhoneIcon,
    WhatsApp as WhatsAppIcon,
    LocationOn as LocationOnIcon,
    MyLocation as MyLocationIcon,
    EditOutlined,
    DeleteOutline,
    Add as AddIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    ShoppingCart as CartIcon,
    ContentCopy as ContentCopyIcon,
    Map as MapIcon,
    Add as ZoomInIcon,
    Remove as ZoomOutIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { businessAPI, orderAPI, invoiceAPI, addressAPI, communityAPI, publicStoreAPI } from '../../api';
import { NewOrderView } from './CustomerOrdersPage';
import PublicStorePage from '../PublicStorePage';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtCurrency = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// /* Online UPI app deep-links — disabled until payment gateway is wired up.
// const UPI_APPS = [
//     { key: 'gpay',    label: 'Google Pay',     scheme: 'tez://upi/pay' },
//     { key: 'phonepe', label: 'PhonePe',        scheme: 'phonepe://pay' },
//     { key: 'paytm',   label: 'Paytm',          scheme: 'paytmmp://pay' },
//     { key: 'other',   label: 'Other UPI apps', scheme: 'upi://pay' },
// ];
// const launchUpiApp = (scheme, vendor, amount) => {
//     const params = [
//         `pa=${encodeURIComponent(vendor.f_upi_id || '').replace(/%40/g, '@')}`,
//         `pn=${encodeURIComponent(vendor.f_name || '')}`,
//         `am=${parseFloat(amount || 0).toFixed(2)}`,
//         `cu=INR`,
//         `tn=${encodeURIComponent('Payment to ' + (vendor.f_name || 'vendor'))}`,
//     ].join('&');
//     window.location.href = `${scheme}?${params}`;
// };
// */

// Navigate the current tab to an order/bill share URL (opens in the same window).
const openShareUrl = async (fetchUrl) => {
    try {
        const res = await fetchUrl();
        const url = res?.data?.share_url;
        if (url) window.location.href = url;
    } catch { }
};

const businessModeLabel = (mode) => {
    if (mode === 'subscription') return 'Subscription';
    if (mode === 'orders') return 'Orders';
    return 'Orders & Subscription';
};

// ── Vendor Card ───────────────────────────────────────────────────────────────

const BANNER_COLORS = [
    ['#1E3A8A', '#1e88e5'],
    ['#2e7d32', '#43a047'],
    ['#e65100', '#fb8c00'],
    ['#6a1b9a', '#8e24aa'],
    ['#b71c1c', '#e53935'],
    ['#00695c', '#00897b'],
    ['#4527a0', '#5e35b1'],
    ['#37474f', '#546e7a'],
    ['#c62828', '#d81b60'],
    ['#0277bd', '#0288d1'],
];

const VendorCard = ({ vendor, onClick, showBalance, registered, compact = false }) => {
    const [from, to] = BANNER_COLORS[vendor.f_id % BANNER_COLORS.length];
    const images = vendor.sample_images || [];
    const logo = vendor.f_logo_url;

    return (
        <Card elevation={2} onClick={onClick}
            sx={{ borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
                transition: 'box-shadow 0.2s', '&:active': { boxShadow: 6 } }}>
            {/* Banner */}
            <Box sx={{ position: 'relative', height: compact ? 80 : 100, overflow: 'hidden' }}>
                {/* A single edge-to-edge image: prefer the logo; if none, use any product
                    photo; if neither, show business initials on the vendor's color. */}
                <Box sx={{ height: '100%', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: logo ? '#fff' : undefined,
                    background: (logo || images.length > 0) ? undefined : `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}>
                    {logo ? (
                        <img src={logo} alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : images.length > 0 ? (
                        <img src={images[0]} alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                        <Typography sx={{ color: '#fff', fontWeight: 800, letterSpacing: 1,
                            textTransform: 'uppercase', fontSize: compact ? '3rem' : '3.75rem', lineHeight: 1 }}>
                            {getInitials(vendor.f_name)}
                        </Typography>
                    )}
                </Box>
                {registered && (
                    <Chip label="My Vendor" size="small"
                        sx={{ position: 'absolute', top: 7, right: 7,
                            bgcolor: 'success.main', color: '#fff', height: 18, fontSize: '0.58rem',
                            fontWeight: 'bold' }} />
                )}
                {showBalance && vendor.outstanding > 0 && (
                    <Chip label={fmtCurrency(vendor.outstanding) + ' due'} size="small"
                        sx={{ position: 'absolute', top: 7, left: 7,
                            bgcolor: 'error.main', color: '#fff', height: 18, fontSize: '0.58rem' }} />
                )}
            </Box>
            {/* Body */}
            <Box sx={{ px: 1.25, pt: 1, pb: 1.25 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>{vendor.f_name}</Typography>
                {vendor.f_city && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {vendor.f_city}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                    <Chip label={businessModeLabel(vendor.f_business_mode)} size="small"
                        variant="outlined" sx={{ fontSize: '0.58rem', height: 18 }} />
                    {vendor.f_phone && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <IconButton size="small" component="a" href={`tel:${vendor.f_phone}`}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0.4, color: '#2563EB', border: '1px solid', borderColor: 'divider' }}>
                                <PhoneIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton size="small" component="a" href={waLink(vendor.f_phone)} target="_blank" rel="noopener"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0.4, color: '#25D366', border: '1px solid', borderColor: 'divider' }}>
                                <WhatsAppIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>
        </Card>
    );
};

// ── Map Picker ────────────────────────────────────────────────────────────────

const DEFAULT_CENTER = { lat: 17.385, lng: 78.4867 }; // Hyderabad fallback

// Max radial (straight-line) distance to associate a pin with a community.
// OSRM walking routes are unreliable for gated communities — internal roads are
// rarely mapped in OpenStreetMap, so routes exit and re-enter through the gate,
// producing inflated distances. Radial distance + 1 km cutoff is the right guard.
const RADIAL_CUTOFF_KM = 1.0;

const MapMoveListener = ({ onMoveEnd }) => {
    useMapEvents({ moveend: (e) => onMoveEnd(e.target.getCenter()) });
    return null;
};

const MapZoomControls = () => {
    const map = useMap();
    return (
        <Box sx={{
            position: 'absolute', right: 12, bottom: 12, zIndex: 1000,
            display: 'flex', flexDirection: 'column', gap: 0.5,
        }}>
            {[{ icon: <ZoomInIcon />, action: () => map.zoomIn() },
              { icon: <ZoomOutIcon />, action: () => map.zoomOut() }].map(({ icon, action }, i) => (
                <Box key={i}
                    onClick={action}
                    sx={{
                        width: 40, height: 40, borderRadius: 1,
                        bgcolor: 'background.paper', boxShadow: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', userSelect: 'none',
                        '&:active': { bgcolor: 'action.selected' },
                    }}>
                    {icon}
                </Box>
            ))}
        </Box>
    );
};

const MapPicker = ({ initialCenter, onConfirm, onClose }) => {
    const [center, setCenter] = useState(initialCenter || DEFAULT_CENTER);
    const [community, setCommunity] = useState(null);
    const [detecting, setDetecting] = useState(false);

    const handleMoveEnd = useCallback(async (latLng) => {
        const { lat, lng } = latLng;
        setCenter({ lat, lng });
        setDetecting(true);
        setCommunity(null);
        try {
            // Try exact boundary match via Google Geocoding first
            const geoRes = await communityAPI.fromLocation(lat, lng);
            if (geoRes.data) {
                setCommunity(geoRes.data);
            } else {
                // Fall back to radial distance (communities without a Google place_id)
                const res = await communityAPI.nearby(lat, lng, RADIAL_CUTOFF_KM);
                const closest = res.data?.[0];
                setCommunity((closest?.distance_km ?? 999) < RADIAL_CUTOFF_KM ? closest : null);
            }
        } catch {}
        setDetecting(false);
    }, []);

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500, display: 'flex', flexDirection: 'column',
            bgcolor: 'background.default' }}>
            {/* Header */}
            <AppBar position="static" color="default" elevation={1}
                sx={{ paddingTop: 'env(safe-area-inset-top, 0px)', flexShrink: 0 }}>
                <Toolbar variant="dense" sx={{ minHeight: 48 }}>
                    <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight="bold">Pick Location</Typography>
                </Toolbar>
            </AppBar>

            {/* Map area */}
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                <MapContainer
                    center={[center.lat, center.lng]}
                    zoom={17}
                    zoomControl={false}
                    style={{ height: '100%', width: '100%' }}
                    attributionControl={false}
                >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapMoveListener onMoveEnd={handleMoveEnd} />
                    <MapZoomControls />
                </MapContainer>

                {/* Fixed center pin — stays put while map moves underneath */}
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', zIndex: 1000,
                    transform: 'translate(-50%, -100%)', pointerEvents: 'none',
                }}>
                    <LocationOnIcon sx={{ fontSize: 52, color: 'primary.main',
                        filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.35))' }} />
                </Box>
                {/* Pin dot shadow on ground */}
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', zIndex: 1000,
                    width: 10, height: 6, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.25)',
                    transform: 'translate(-50%, -50%)', pointerEvents: 'none',
                }} />

                {/* Hint badge */}
                <Box sx={{
                    position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 1000, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                    px: 2, py: 0.5, borderRadius: 3,
                    pointerEvents: 'none',
                }}>
                    <Typography variant="caption">Move map to adjust pin</Typography>
                </Box>
            </Box>

            {/* Bottom confirmation bar */}
            <Box sx={{
                px: 2, pt: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
                bgcolor: 'background.paper', boxShadow: '0 -2px 10px rgba(0,0,0,0.12)',
                flexShrink: 0,
            }}>
                {detecting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minHeight: 48 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Detecting area…</Typography>
                    </Box>
                ) : community ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minHeight: 48,
                        p: 1, bgcolor: 'success.lighter', borderRadius: 1.5, border: '1px solid',
                        borderColor: 'success.light' }}>
                        <LocationOnIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="bold" noWrap>{community.f_name}</Typography>
                            {community.f_city && (
                                <Typography variant="caption" color="text.secondary">
                                    {community.f_city}
                                </Typography>
                            )}
                        </Box>
                        <IconButton size="small" onClick={() => setCommunity(null)}>
                            <DeleteOutline fontSize="small" color="action" />
                        </IconButton>
                    </Box>
                ) : (
                    <Box sx={{ mb: 1.5, minHeight: 48, display: 'flex', alignItems: 'center',
                        p: 1, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                        <LocationOnIcon fontSize="small" color="disabled" sx={{ mr: 1, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                            No community found nearby — GPS coordinates will be saved
                        </Typography>
                    </Box>
                )}
                <Button fullWidth variant="contained" size="large"
                    onClick={() => onConfirm(community, center)}>
                    Confirm Location
                </Button>
            </Box>
        </Box>
    );
};

// ── Manage Address Page ───────────────────────────────────────────────────────

const ADDR_LABELS = ['Home', 'Office', 'Other'];

const ManageAddressPage = ({ onBack }) => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQ, setSearchQ] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [detecting, setDetecting] = useState(false);
    // form state
    const [showForm, setShowForm] = useState(false);
    const [formAddr, setFormAddr] = useState(null);
    const [selectedCommunity, setSelectedCommunity] = useState(null);
    const [gpsCoords, setGpsCoords] = useState(null);
    const [unit, setUnit] = useState('');
    const [label, setLabel] = useState('Home');
    const [saving, setSaving] = useState(false);
    const [formSearchQ, setFormSearchQ] = useState('');
    const [formSearchResults, setFormSearchResults] = useState([]);
    const [showMap, setShowMap] = useState(false);
    const [mapInitCenter, setMapInitCenter] = useState(null);
    const searchTimer = React.useRef(null);
    const formSearchTimer = React.useRef(null);

    const reload = useCallback(async () => {
        try {
            const res = await addressAPI.list();
            setAddresses(res.data || []);
        } catch {} finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const onSearchChange = (q) => {
        setSearchQ(q);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        if (q.length < 2) { setSearchResults([]); return; }
        searchTimer.current = setTimeout(async () => {
            try {
                const res = await communityAPI.search(q);
                setSearchResults(res.data || []);
            } catch {}
        }, 400);
    };

    const onFormSearchChange = (q) => {
        setFormSearchQ(q);
        if (formSearchTimer.current) clearTimeout(formSearchTimer.current);
        if (q.length < 2) { setFormSearchResults([]); return; }
        formSearchTimer.current = setTimeout(async () => {
            try {
                const res = await communityAPI.search(q);
                setFormSearchResults(res.data || []);
            } catch {}
        }, 400);
    };

    const openForm = (community, coords, existingAddr) => {
        setSelectedCommunity(community);
        setGpsCoords(coords);
        setFormAddr(existingAddr || null);
        setUnit(existingAddr?.f_unit || '');
        setLabel(existingAddr?.f_label || 'Home');
        setFormSearchQ('');
        setFormSearchResults([]);
        setSaving(false);
        setShowForm(true);
    };

    const detectLocation = () => {
        if (!navigator.geolocation) return;
        setDetecting(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude: lat, longitude: lng } = pos.coords;
                let comm = null;
                try {
                    const geoRes = await communityAPI.fromLocation(lat, lng);
                    if (geoRes.data) {
                        comm = geoRes.data;
                    } else {
                        const res = await communityAPI.nearby(lat, lng, RADIAL_CUTOFF_KM);
                        const closest = res.data?.[0];
                        if (closest && (closest.distance_km ?? 999) < RADIAL_CUTOFF_KM) comm = closest;
                    }
                } catch {}
                setDetecting(false);
                openForm(comm, { lat, lng }, null);
            },
            () => setDetecting(false),
            { timeout: 10000 }
        );
    };

    const openMapPicker = () => {
        if (!navigator.geolocation) {
            setMapInitCenter(DEFAULT_CENTER);
            setShowMap(true);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setMapInitCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setShowMap(true);
            },
            () => {
                setMapInitCenter(DEFAULT_CENTER);
                setShowMap(true);
            },
            { timeout: 5000 }
        );
    };

    const handleSave = async () => {
        if (!unit.trim()) return;
        setSaving(true);
        try {
            const payload = {
                f_unit: unit.trim(),
                f_label: label,
                f_community_id: selectedCommunity?.f_id || null,
                f_latitude: gpsCoords?.lat || null,
                f_longitude: gpsCoords?.lng || null,
            };
            if (formAddr) {
                await addressAPI.update(formAddr.f_id, { ...payload, f_default: formAddr.f_default });
            } else {
                await addressAPI.create(payload);
            }
            setShowForm(false);
            setSearchQ('');
            setSearchResults([]);
            await reload();
        } catch {}
        setSaving(false);
    };

    const addrText = (a) => [a.f_unit, a.community_name].filter(Boolean).join(', ');

    return (
        <>
            <AppBar position="fixed" color="default" elevation={1}
                sx={{ top: 0, paddingTop: 'env(safe-area-inset-top, 0px)' }}>
                <Toolbar variant="dense" sx={{ minHeight: 48 }}>
                    <IconButton edge="start" onClick={onBack} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight="bold">Manage Address</Typography>
                </Toolbar>
            </AppBar>

            <Box sx={{ mt: 'calc(48px + env(safe-area-inset-top, 0px))', pb: 6 }}>
                {/* Search bar */}
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                    <TextField
                        fullWidth size="small"
                        placeholder="Search for your society / area"
                        value={searchQ}
                        onChange={(e) => onSearchChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Search results */}
                {searchResults.length > 0 && (
                    <Box sx={{ mx: 2, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                        {searchResults.map(c => (
                            <ListItemButton key={c.f_id} dense
                                onClick={() => {
                                    setSearchQ('');
                                    setSearchResults([]);
                                    openForm(c, null, null);
                                }}>
                                <LocationOnIcon sx={{ mr: 1.5, fontSize: 18 }} color="action" />
                                <ListItemText
                                    primary={c.f_name} secondary={c.f_city || ''}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }} />
                            </ListItemButton>
                        ))}
                    </Box>
                )}

                {/* Find my location */}
                <ListItemButton onClick={detectLocation} disabled={detecting} sx={{ px: 2, py: 1.5 }}>
                    <MyLocationIcon sx={{ mr: 2, fontSize: 22 }} color="primary" />
                    <ListItemText
                        primary={detecting ? 'Detecting your location…' : 'Find my location'}
                        secondary={detecting ? null : 'Use GPS to auto-detect'}
                        primaryTypographyProps={{ variant: 'body2', color: 'primary.main', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption' }} />
                    {detecting && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </ListItemButton>

                {/* Pick from map */}
                <ListItemButton onClick={openMapPicker} sx={{ px: 2, py: 1.5 }}>
                    <MapIcon sx={{ mr: 2, fontSize: 22 }} color="primary" />
                    <ListItemText
                        primary="Pick from map"
                        secondary="Drag the map to pin your location"
                        primaryTypographyProps={{ variant: 'body2', color: 'primary.main', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption' }} />
                </ListItemButton>

                <Divider />

                {/* Add new address */}
                <ListItemButton onClick={() => openForm(null, null, null)} sx={{ px: 2, py: 1.5 }}>
                    <AddIcon sx={{ mr: 2, fontSize: 22 }} color="primary" />
                    <ListItemText
                        primary="Add new Address"
                        primaryTypographyProps={{ variant: 'body2', color: 'primary.main', fontWeight: 'medium' }} />
                </ListItemButton>

                <Divider />

                {/* Saved addresses */}
                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : addresses.length > 0 ? (
                    <Box sx={{ px: 2, pt: 2 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            Saved Addresses
                        </Typography>
                        {addresses.map((a) => (
                            <Box key={a.f_id}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5 }}>
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                        bgcolor: a.f_default ? 'primary.lighter' : 'action.hover',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <LocationOnIcon fontSize="small" color={a.f_default ? 'primary' : 'action'} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                            <Typography variant="caption" fontWeight="bold"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                {a.f_label}
                                            </Typography>
                                            {a.f_default && (
                                                <Chip label="Default" size="small"
                                                    sx={{ height: 14, fontSize: '0.55rem',
                                                        bgcolor: 'primary.main', color: '#fff' }} />
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                                            {addrText(a)}
                                        </Typography>
                                        {!a.f_default && (
                                            <Button size="small" variant="text"
                                                onClick={async () => { await addressAPI.setDefault(a.f_id); reload(); }}
                                                sx={{ mt: 0.25, p: 0, fontSize: '0.72rem', textTransform: 'none', color: 'primary.main' }}>
                                                Set as default
                                            </Button>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                        <IconButton size="small"
                                            onClick={() => openForm(
                                                a.f_community_id
                                                    ? { f_id: a.f_community_id, f_name: a.community_name, f_city: a.community_city }
                                                    : null,
                                                a.f_latitude ? { lat: a.f_latitude, lng: a.f_longitude } : null,
                                                a
                                            )}>
                                            <EditOutlined fontSize="small" color="action" />
                                        </IconButton>
                                        <IconButton size="small"
                                            onClick={async () => { await addressAPI.remove(a.f_id); reload(); }}>
                                            <DeleteOutline fontSize="small" color="action" />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Divider />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <LocationOnIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">No saved addresses yet</Typography>
                    </Box>
                )}
            </Box>

            {/* Add / Edit form drawer */}
            <Drawer
                anchor="bottom"
                open={showForm}
                onClose={() => setShowForm(false)}
                sx={{ zIndex: 1400 }}
                PaperProps={{ sx: {
                    borderTopLeftRadius: 16, borderTopRightRadius: 16,
                    p: 2, pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                } }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
                    {formAddr ? 'Edit Address' : 'Add Address'}
                </Typography>

                {/* Selected community display or search */}
                {selectedCommunity ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <LocationOnIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                            {selectedCommunity.f_name}{selectedCommunity.f_city ? `, ${selectedCommunity.f_city}` : ''}
                        </Typography>
                        <IconButton size="small" onClick={() => { setSelectedCommunity(null); setFormSearchQ(''); setFormSearchResults([]); }}>
                            <DeleteOutline fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <>
                        <TextField size="small" fullWidth
                            placeholder="Search society / area (optional)"
                            value={formSearchQ}
                            onChange={(e) => onFormSearchChange(e.target.value)}
                            sx={{ mb: formSearchResults.length > 0 ? 0.5 : 1.5 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon fontSize="small" color="action" /></InputAdornment> }}
                        />
                        {formSearchResults.length > 0 && (
                            <Box sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 140, overflow: 'auto' }}>
                                {formSearchResults.map(c => (
                                    <ListItemButton key={c.f_id} dense
                                        onClick={() => { setSelectedCommunity(c); setFormSearchQ(''); setFormSearchResults([]); }}>
                                        <ListItemText primary={c.f_name} secondary={c.f_city}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                            secondaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItemButton>
                                ))}
                            </Box>
                        )}
                    </>
                )}

                <TextField size="small" fullWidth
                    label="Door No / Flat / Villa"
                    placeholder="e.g. Tower A-304 or Villa 12"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    sx={{ mb: 1.5 }}
                />

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {ADDR_LABELS.map(l => (
                        <Chip key={l} label={l} size="small"
                            onClick={() => setLabel(l)}
                            color={label === l ? 'primary' : 'default'}
                            variant={label === l ? 'filled' : 'outlined'} />
                    ))}
                </Box>

                <Button fullWidth variant="contained" onClick={handleSave} disabled={!unit.trim() || saving}>
                    {saving ? <CircularProgress size={18} /> : 'Save Address'}
                </Button>
            </Drawer>

            {/* Map picker — full-screen overlay */}
            {showMap && mapInitCenter && (
                <MapPicker
                    initialCenter={mapInitCenter}
                    onClose={() => setShowMap(false)}
                    onConfirm={(community, coords) => {
                        setShowMap(false);
                        openForm(community, coords, null);
                    }}
                />
            )}
        </>
    );
};

// ── Shop Tab ──────────────────────────────────────────────────────────────────

export const ShopTab = ({ vendors, onSelectVendor, onManageAddress, apiMode = 'auth' }) => {
    const [locState, setLocState] = useState('idle');
    const [community, setCommunity] = useState(null);
    const [communityVendors, setCommunityVendors] = useState([]);
    const [communityLoading, setCommunityLoading] = useState(false);
    const [filter, setFilter] = useState('All'); // 'All' | 'My Vendors' | 'Others'
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [locationContext, setLocationContext] = useState(null); // null = GPS, or address obj
    const [locDrawerOpen, setLocDrawerOpen] = useState(false);
    const [drawerSearchQ, setDrawerSearchQ] = useState('');
    const [drawerSearchResults, setDrawerSearchResults] = useState([]);
    const [drawerSearchLoading, setDrawerSearchLoading] = useState(false);

    const myVendorIds = useMemo(() => new Set(vendors.map(v => v.f_id)), [vendors]);

    // Merged deduplicated list — registered vendors first, then community-only
    const allVendors = useMemo(() => {
        const seen = new Set();
        const list = [];
        [...vendors, ...communityVendors].forEach(v => {
            if (!seen.has(v.f_id)) {
                seen.add(v.f_id);
                list.push({ ...v, registered: myVendorIds.has(v.f_id) });
            }
        });
        return list;
    }, [vendors, communityVendors, myVendorIds]);

    const filtered = useMemo(() => {
        if (filter === 'My Vendors') return allVendors.filter(v => v.registered);
        if (filter === 'Others') return allVendors.filter(v => !v.registered);
        return allVendors;
    }, [allVendors, filter]);

    const detectAndLoad = useCallback(() => {
        if (!navigator.geolocation) { setLocState('unsupported'); return; }
        setLocState('detecting');
        setCommunity(null);
        setCommunityVendors([]);

        // iOS Chrome never calls any callback if the location permission dialog
        // is blocked or silently dismissed — the native timeout option only starts
        // counting after permission is granted. Force a fallback after 12 s.
        let settled = false;
        const fallbackTimer = setTimeout(() => {
            if (!settled) { settled = true; setLocState('unavailable'); }
        }, 12000);

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                if (settled) return;
                settled = true;
                clearTimeout(fallbackTimer);
                setLocState('found');
                setCommunityLoading(true);
                try {
                    let comm = null;
                    if (apiMode === 'guest') {
                        const { data } = await publicStoreAPI.getCommunitiesNearby(
                            pos.coords.latitude, pos.coords.longitude, 5.0
                        );
                        comm = (data || [])[0] || null;
                    } else {
                        const { data } = await businessAPI.getCommunityNearby(
                            pos.coords.latitude, pos.coords.longitude
                        );
                        comm = data;
                    }
                    if (comm) {
                        setCommunity(comm);
                        const getVendors = apiMode === 'guest'
                            ? publicStoreAPI.getCommunityVendors
                            : businessAPI.getCommunityVendors;
                        const { data: vList } = await getVendors(comm.f_id);
                        setCommunityVendors(vList || []);
                    }
                } catch { } finally {
                    setCommunityLoading(false);
                }
            },
            (err) => {
                if (settled) return;
                settled = true;
                clearTimeout(fallbackTimer);
                setLocState(err.code === 1 ? 'denied' : 'unavailable');
            },
            { timeout: 10000, enableHighAccuracy: false }
        );
    }, [apiMode]);

    // Load saved addresses (auth only); if default has a community, use it instead of GPS
    useEffect(() => {
        if (apiMode === 'guest') { detectAndLoad(); return; }
        addressAPI.list().then(res => {
            const addrs = res.data || [];
            setSavedAddresses(addrs);
            const def = addrs.find(a => a.f_default);
            if (def?.f_community_id) {
                setLocationContext(def);
            } else {
                detectAndLoad();
            }
        }).catch(() => detectAndLoad());
    }, [detectAndLoad, apiMode]);

    const handleDrawerSearch = useCallback(async (q) => {
        setDrawerSearchQ(q);
        if (!q.trim() || q.trim().length < 2) { setDrawerSearchResults([]); return; }
        setDrawerSearchLoading(true);
        try {
            const { data } = await publicStoreAPI.searchCommunities(q.trim());
            setDrawerSearchResults(data || []);
        } catch { setDrawerSearchResults([]); }
        finally { setDrawerSearchLoading(false); }
    }, []);

    const selectCommunityFromSearch = useCallback((c) => {
        setLocDrawerOpen(false);
        setDrawerSearchQ('');
        setDrawerSearchResults([]);
        setLocState('found');
        setCommunity(c);
        setCommunityVendors([]);
        setCommunityLoading(true);
        (apiMode === 'guest' ? publicStoreAPI.getCommunityVendors : businessAPI.getCommunityVendors)(c.f_id)
            .then(res => setCommunityVendors(res.data || []))
            .catch(() => {})
            .finally(() => setCommunityLoading(false));
    }, [apiMode]);

    // When a saved address is selected as location context, load its community vendors
    useEffect(() => {
        if (!locationContext?.f_community_id) return;
        setLocState('found');
        setCommunityLoading(true);
        setCommunity({ f_id: locationContext.f_community_id, f_name: locationContext.community_name });
        setCommunityVendors([]);
        businessAPI.getCommunityVendors(locationContext.f_community_id)
            .then(res => setCommunityVendors(res.data || []))
            .catch(() => {})
            .finally(() => setCommunityLoading(false));
    }, [locationContext]);

    return (
        <Box>
            {/* Location context chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Chip
                    icon={<LocationOnIcon sx={{ fontSize: '14px !important' }} />}
                    label={
                        locationContext
                            ? `${locationContext.f_label} · ${locationContext.f_unit}`
                            : community?.f_name || (locState === 'detecting' ? 'Detecting…' : 'Near me')
                    }
                    onClick={() => setLocDrawerOpen(true)}
                    color={locationContext ? 'primary' : 'default'}
                    variant="outlined"
                    size="small"
                    sx={{ maxWidth: '80%', cursor: 'pointer' }}
                    deleteIcon={<ChevronRightIcon />}
                    onDelete={() => setLocDrawerOpen(true)}
                />
                {locState === 'detecting' && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Box>

            {/* Location picker drawer */}
            <Drawer anchor="bottom" open={locDrawerOpen} onClose={() => setLocDrawerOpen(false)}
                sx={{ zIndex: 1400 }}
                PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2, pb: 'calc(16px + env(safe-area-inset-bottom, 0px))' } }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>Show vendors near</Typography>
                <ListItemButton onClick={() => { setLocationContext(null); setLocDrawerOpen(false); detectAndLoad(); }}
                    sx={{ borderRadius: 1, mb: 0.5 }}>
                    <MyLocationIcon sx={{ mr: 1.5, fontSize: 20 }} color="primary" />
                    <ListItemText primary="Current location" secondary="Detect via GPS"
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }} />
                </ListItemButton>
                {apiMode === 'guest' ? (
                    <>
                        <TextField size="small" fullWidth sx={{ mt: 1 }}
                            placeholder="Search your area / community…"
                            value={drawerSearchQ}
                            onChange={e => handleDrawerSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {drawerSearchLoading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                                    </InputAdornment>
                                ),
                                endAdornment: drawerSearchQ ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => { setDrawerSearchQ(''); setDrawerSearchResults([]); }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        {drawerSearchResults.map(c => (
                            <ListItemButton key={c.f_id} sx={{ borderRadius: 1, mt: 0.5 }}
                                onClick={() => selectCommunityFromSearch(c)}>
                                <LocationOnIcon sx={{ mr: 1.5, fontSize: 16 }} color="action" />
                                <ListItemText primary={c.f_name} secondary={c.f_city}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }} />
                            </ListItemButton>
                        ))}
                        {drawerSearchQ.trim().length >= 2 && !drawerSearchLoading && drawerSearchResults.length === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 0.5 }}>
                                No communities found for "{drawerSearchQ}"
                            </Typography>
                        )}
                    </>
                ) : (
                    <>
                        {savedAddresses.map(a => (
                            <ListItemButton key={a.f_id}
                                selected={locationContext?.f_id === a.f_id}
                                onClick={() => { setLocationContext(a); setLocDrawerOpen(false); }}
                                sx={{ borderRadius: 1, mb: 0.5 }}>
                                <LocationOnIcon sx={{ mr: 1.5, fontSize: 20 }} color="action" />
                                <ListItemText
                                    primary={`${a.f_label} · ${a.f_unit}`}
                                    secondary={a.community_name || ''}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }} />
                            </ListItemButton>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <ListItemButton
                            onClick={() => { setLocDrawerOpen(false); onManageAddress?.(); }}
                            sx={{ borderRadius: 1 }}>
                            <AddIcon sx={{ mr: 1.5, fontSize: 20 }} color="primary" />
                            <ListItemText primary="Manage Addresses"
                                secondary="Add, edit or set default address"
                                primaryTypographyProps={{ variant: 'body2', color: 'primary.main', fontWeight: 'medium' }}
                                secondaryTypographyProps={{ variant: 'caption' }} />
                            <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </ListItemButton>
                    </>
                )}
            </Drawer>

            {/* Location status */}
            {locState === 'detecting' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2, borderRadius: 2, bgcolor: 'info.lighter', border: '1px dashed', borderColor: 'info.light' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="info.main">Detecting your location…</Typography>
                </Box>
            )}
            {(locState === 'denied' || locState === 'unsupported' || locState === 'unavailable') && (
                <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.light' }}>
                    <Typography variant="body2" color="warning.dark" fontWeight="bold" sx={{ mb: 0.5 }}>
                        📍 {locState === 'denied' ? 'Location permission blocked' : locState === 'unavailable' ? 'Could not detect location' : 'Location not supported'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {locState === 'denied' ? 'Allow location in your browser settings to discover nearby vendors.'
                            : 'GPS unavailable. Only your registered vendors are shown.'}
                    </Typography>
                    {(locState === 'denied' || locState === 'unavailable') && (
                        <Button size="small" variant="outlined" color="warning" sx={{ mt: 1 }} onClick={detectAndLoad}>Retry</Button>
                    )}
                </Box>
            )}
            {communityLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            )}

            {/* Filter chips — auth mode only */}
            {!communityLoading && allVendors.length > 0 && apiMode !== 'guest' && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {['All', 'My Vendors', 'Others'].map(f => (
                        <Chip
                            key={f}
                            label={f}
                            size="small"
                            onClick={() => setFilter(f)}
                            color={filter === f ? 'primary' : 'default'}
                            variant={filter === f ? 'filled' : 'outlined'}
                        />
                    ))}
                </Box>
            )}

            {/* Combined vendor list */}
            {!communityLoading && (
                filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <ShopIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
                        <Typography variant="body1" color="text.secondary">
                            {filter === 'My Vendors' ? 'No registered vendors yet' : filter === 'Others' ? 'No other vendors nearby' : 'No vendors found'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gap: 1.5,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' } }}>
                        {filtered.map(v => (
                            <VendorCard
                                key={v.f_id}
                                vendor={v}
                                onClick={() => onSelectVendor(v)}
                                registered={v.registered}
                            />
                        ))}
                    </Box>
                )
            )}
        </Box>
    );
};

// ── Home Tab ──────────────────────────────────────────────────────────────────

// WhatsApp deep link — strips non-digits and adds India country code for 10-digit numbers.
const waLink = (phone) => {
    const d = String(phone || '').replace(/\D/g, '');
    const num = d.length === 10 ? '91' + d : d;
    return `https://wa.me/${num}`;
};

const STATUS_COLORS_MAP = { created: 'info', accepted: 'primary', delivered: 'success', cancelled: 'error' };
const STATUS_ACCENT = { created: '#0288D1', accepted: '#2563EB', delivered: '#2E7D32', cancelled: '#D32F2F' };
const PAYMENT_COLORS_MAP = { due: 'error', paid_directly: 'success', pay_later: 'warning', invoice: 'info' };
const BILL_COLORS_MAP = { Paid: 'success', Partial: 'warning', Unpaid: 'error' };
const BILL_ACCENT = { Paid: '#2E7D32', Partial: '#ED6C02', Unpaid: '#D32F2F' };
const fmtAmt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// ── Customer Plans (self-service: pause / resume / change qty / stop) ────────────

const PLAN_STATUS_STYLE = {
    Active:        { label: 'Active',   color: '#2E7D32', bg: '#E8F5E9' },
    Paused:        { label: 'Paused',   color: '#E8770C', bg: '#FFF3E0' },
    Ended:         { label: 'Stopped',  color: '#9E9E9E', bg: '#F5F5F5' },
    'Not started': { label: 'Upcoming', color: '#1E3A8A', bg: '#E3F2FD' },
};

// Local-time YYYY-MM-DD helpers (avoid UTC day-shift from toISOString in IST).
const toISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};
const todayISO = () => toISODate(new Date());
const tomorrowISO = () => { const d = new Date(); d.setDate(d.getDate() + 1); return toISODate(d); };
const addDaysISO = (iso, n) => { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n); return toISODate(d); };
const daysBetweenISO = (a, b) => Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000);
const fmtDay = (iso) => {
    if (!iso) return '';
    try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
    catch { return iso; }
};

// Plans is for seeing and changing the plans you already have. Starting a NEW plan goes through
// the store's plan cart/checkout (onShopVendor), so there's one sign-up flow in the whole app.
const CustomerPlansSection = ({ vendors, onShopVendor }) => {
    const [loading, setLoading] = useState(true);
    const [byVendor, setByVendor] = useState([]);   // [{ vendor, plans, requests }]
    const [error, setError] = useState('');
    const [snack, setSnack] = useState('');
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState(null);      // { vendorId, plan, type }
    const [form, setForm] = useState({ date: tomorrowISO(), qty: 1, duration: '2', endDate: '' });

    const load = useCallback(async () => {
        if (!vendors || vendors.length === 0) { setByVendor([]); setLoading(false); return; }
        setLoading(true);
        setError('');
        try {
            const results = await Promise.all(vendors.map(async v => {
                const [plans, requests] = await Promise.all([
                    businessAPI.getMySubscriptions(v.f_id).then(r => r.data || []).catch(() => []),
                    businessAPI.getMyPlanRequests(v.f_id).then(r => r.data || []).catch(() => []),
                ]);
                return { vendor: v, plans, requests };
            }));
            setByVendor(results);
        } catch { setError('Could not load your plans'); }
        finally { setLoading(false); }
    }, [vendors]);

    useEffect(() => { load(); }, [load]);

    const openDialog = (vendorId, plan, type) => {
        // Stopping can take effect today (deliveries stop the next day); other changes start tomorrow.
        const start = type === 'stop' ? todayISO() : tomorrowISO();
        setForm({ date: start, qty: plan.quantity || 1, duration: '2', endDate: addDaysISO(tomorrowISO(), 2) });
        setDialog({ vendorId, plan, type });
    };

    // Reopen the matching dialog pre-filled from an existing scheduled change, so Confirm
    // replaces it (cancel the old entry/entries, then create the new ones).
    const openEditDialog = (vendorId, plan, it) => {
        if (it.kind === 'pause') {
            let duration = 'forever';
            let endDate = addDaysISO(it.from, 2);
            if (it.to) {
                const d = daysBetweenISO(it.from, it.to);
                if (d >= 1 && d <= 7) duration = String(d);
                else { duration = 'until'; endDate = it.to; }
            }
            setForm({ date: it.from, qty: plan.quantity || 1, duration, endDate });
            setDialog({ vendorId, plan, type: 'pause', editIds: it.ids });
            return;
        }
        const type = it.action === 'Change in Quantity' ? 'qty'
            : it.action === 'End' ? 'stop'
            : it.action === 'Resume' ? 'resume' : null;
        if (!type) return;
        setForm({ date: it.from, qty: it.qty || plan.quantity || 1, duration: '2', endDate: addDaysISO(it.from, 2) });
        setDialog({ vendorId, plan, type, editIds: it.ids });
    };

    const closeDialog = () => setDialog(null);

    // Pair each Suspend with the next Resume (same product) so a fixed-length pause reads as one row.
    const pairScheduled = (changes = []) => {
        const sorted = [...changes].sort((a, b) => a.effective_from_date.localeCompare(b.effective_from_date));
        const used = new Set();
        const items = [];
        sorted.forEach((c, i) => {
            if (used.has(c.log_id) || c.action !== 'Suspend') return;
            used.add(c.log_id);
            const resume = sorted.find((x, j) => j > i && x.action === 'Resume' && !used.has(x.log_id));
            if (resume) {
                used.add(resume.log_id);
                items.push({ kind: 'pause', from: c.effective_from_date, to: resume.effective_from_date, ids: [c.log_id, resume.log_id] });
            } else {
                items.push({ kind: 'pause', from: c.effective_from_date, to: null, ids: [c.log_id] });
            }
        });
        sorted.forEach(c => {
            if (used.has(c.log_id)) return;
            items.push({ kind: 'single', action: c.action, from: c.effective_from_date, qty: c.quantity, ids: [c.log_id] });
        });
        return items;
    };

    const scheduledLabel = (it) => {
        if (it.kind === 'pause') {
            return it.to
                ? `Pause ${fmtDay(it.from)} → resumes ${fmtDay(it.to)}`
                : `Pause from ${fmtDay(it.from)} — until you resume`;
        }
        if (it.action === 'Resume') return `Resumes ${fmtDay(it.from)}`;
        if (it.action === 'Change in Quantity') return `Change to ${it.qty} from ${fmtDay(it.from)}`;
        if (it.action === 'End') return `Stops ${fmtDay(it.from)}`;
        return `Change from ${fmtDay(it.from)}`;
    };

    const act = (vendorId, productId, action, quantity, date) =>
        businessAPI.customerSubscriptionAction(vendorId, {
            f_business_product_id: productId, f_action: action,
            f_quantity: quantity, f_effective_from_date: date,
        });

    const submit = async () => {
        if (!dialog) return;
        const { vendorId, plan, type, editIds } = dialog;
        // Work out (and validate) the pause resume date before we cancel anything, so an edit
        // that fails validation leaves the existing schedule untouched.
        let resumeDate = null;
        if (type === 'pause') {
            if (form.duration === 'until') resumeDate = form.endDate;
            else if (form.duration !== 'forever') resumeDate = addDaysISO(form.date, Number(form.duration));
            // 'forever' → no resume record
            if (resumeDate && resumeDate <= form.date) {
                setSnack('Please pick a resume date after the pause date.');
                return;
            }
        }

        setBusy(true);
        try {
            // Editing an existing scheduled change: drop the old entry (or Suspend+Resume pair)
            // before creating the replacement, so we never leave both scheduled at once.
            if (editIds && editIds.length) {
                for (const id of editIds) {
                    try { await businessAPI.cancelScheduledSubscriptionChange(vendorId, id); } catch {}
                }
            }
            if (type === 'pause') {
                const res = await act(vendorId, plan.product_id, 'Suspend', plan.quantity || 1, form.date);
                if (resumeDate) {
                    try {
                        await act(vendorId, plan.product_id, 'Resume', plan.quantity || 1, resumeDate);
                    } catch (e) {
                        // Roll back the pause so we never leave a dangling open-ended pause.
                        const logId = res?.data?.log_id;
                        if (logId) { try { await businessAPI.cancelScheduledSubscriptionChange(vendorId, logId); } catch {} }
                        throw e;
                    }
                }
                setSnack('Pause scheduled. Your vendor has been told.');
            } else if (type === 'resume') {
                await act(vendorId, plan.product_id, 'Resume', plan.quantity || 1, form.date);
                setSnack('Resume scheduled. Your vendor has been told.');
            } else if (type === 'qty') {
                await act(vendorId, plan.product_id, 'Change in Quantity', form.qty, form.date);
                setSnack('Quantity change scheduled. Your vendor has been told.');
            } else if (type === 'stop') {
                await act(vendorId, plan.product_id, 'End', plan.quantity || 1, form.date);
                setSnack('Plan will stop. Your vendor has been told.');
            }
            closeDialog();
            await load();
        } catch (e) {
            setSnack(e?.response?.data?.detail || 'Could not save. Please try again.');
        } finally { setBusy(false); }
    };

    const undo = async (vendorId, ids) => {
        setBusy(true);
        try {
            for (const id of ids) {
                try { await businessAPI.cancelScheduledSubscriptionChange(vendorId, id); } catch {}
            }
            setSnack('Change undone.');
            await load();
        } catch (e) {
            setSnack(e?.response?.data?.detail || 'Could not undo. Please try again.');
        } finally { setBusy(false); }
    };

    if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} /></Box>;
    if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
    if (byVendor.length === 0) return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">No vendors yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Contact your vendor to get added</Typography>
        </Box>
    );

    const dlgName = dialog?.plan?.product_name || 'plan';
    const dlgTitle = dialog
        ? (dialog.editIds
            ? ({ pause: 'Edit pause', resume: 'Edit resume', qty: 'Edit quantity change', stop: 'Edit stop' }[dialog.type])
            : ({ pause: `Pause ${dlgName}`, resume: `Resume ${dlgName}`, qty: 'Change quantity', stop: `Stop ${dlgName}` }[dialog.type]))
        : '';

    return (
        <Box>
            {byVendor.map(({ vendor, plans, requests }) => (
                <Box key={vendor.f_id} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BusinessLogo src={vendor.f_logo_url} name={vendor.f_name} size={24} radius="7px" />
                        <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ flex: 1, minWidth: 0 }}>{vendor.f_name}</Typography>
                        <Button size="small" variant="outlined" disabled={busy} startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                            onClick={() => onShopVendor && onShopVendor(vendor, 'subscription')}
                            sx={{ fontSize: '0.7rem', py: 0.2, px: 1, textTransform: 'none', fontWeight: 700, flexShrink: 0 }}>
                            Add a subscription
                        </Button>
                    </Box>

                    {(requests || []).map(req => (
                        <Card key={`req-${req.request_id}`} variant="outlined"
                            sx={{ mb: 1.25, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: '#1E3A8A', bgcolor: 'action.hover' }}>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.25 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {(req.items || []).map(it => (
                                        <Typography key={it.product_id} variant="body2" fontWeight={800} noWrap>
                                            {it.product_name} · {it.quantity}
                                        </Typography>
                                    ))}
                                    {req.requested_date && (
                                        <Typography variant="caption" color="text.secondary">
                                            from {fmtDay(req.requested_date)}
                                        </Typography>
                                    )}
                                </Box>
                                <Chip label="Waiting for vendor" size="small"
                                    sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, color: '#1E3A8A', bgcolor: '#E3F2FD' }} />
                            </CardContent>
                        </Card>
                    ))}

                    {plans.length === 0 && (requests || []).length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            No subscriptions yet — tap "Add a subscription" to pick from this vendor's shop.
                        </Typography>
                    )}

                    {plans.map(plan => {
                        const st = PLAN_STATUS_STYLE[plan.status] || PLAN_STATUS_STYLE.Active;
                        const scheduled = pairScheduled(plan.scheduled_changes);
                        const isActive = plan.status === 'Active';
                        const isPaused = plan.status === 'Paused';
                        return (
                            <Card key={plan.product_id} variant="outlined"
                                sx={{ mb: 1.25, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: st.color }}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.25 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={800} noWrap>{plan.product_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Qty: {plan.quantity}
                                                {plan.schedule_label && plan.schedule_label !== 'Every day'
                                                    ? ` · ${plan.schedule_label}` : ''}
                                            </Typography>
                                        </Box>
                                        <Chip label={st.label} size="small"
                                            sx={{ height: 22, fontSize: '0.66rem', fontWeight: 700, color: st.color, bgcolor: st.bg }} />
                                    </Box>

                                    {scheduled.length > 0 && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                                            {scheduled.map((it, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                                                        ⏱ {scheduledLabel(it)}
                                                    </Typography>
                                                    <Button size="small" variant="text" disabled={busy}
                                                        onClick={() => openEditDialog(vendor.f_id, plan, it)}
                                                        sx={{ p: 0, minWidth: 0, fontSize: '0.7rem', textTransform: 'none', fontWeight: 700 }}>
                                                        Edit
                                                    </Button>
                                                    <Button size="small" variant="text" color="error" disabled={busy}
                                                        onClick={() => undo(vendor.f_id, it.ids)}
                                                        sx={{ p: 0, minWidth: 0, fontSize: '0.7rem', textTransform: 'none', fontWeight: 700 }}>
                                                        Undo
                                                    </Button>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {(isActive || isPaused) && (
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
                                            {isActive && (
                                                <Button size="small" variant="outlined" color="warning" disabled={busy}
                                                    onClick={() => openDialog(vendor.f_id, plan, 'pause')}
                                                    sx={{ fontSize: '0.72rem', py: 0.3, textTransform: 'none', fontWeight: 700 }}>
                                                    Pause
                                                </Button>
                                            )}
                                            {isPaused && (
                                                <Button size="small" variant="outlined" color="success" disabled={busy}
                                                    onClick={() => openDialog(vendor.f_id, plan, 'resume')}
                                                    sx={{ fontSize: '0.72rem', py: 0.3, textTransform: 'none', fontWeight: 700 }}>
                                                    Resume
                                                </Button>
                                            )}
                                            {isActive && (
                                                <Button size="small" variant="outlined" disabled={busy}
                                                    onClick={() => openDialog(vendor.f_id, plan, 'qty')}
                                                    sx={{ fontSize: '0.72rem', py: 0.3, textTransform: 'none', fontWeight: 700 }}>
                                                    Change qty
                                                </Button>
                                            )}
                                            <Button size="small" variant="text" color="error" disabled={busy}
                                                onClick={() => openDialog(vendor.f_id, plan, 'stop')}
                                                sx={{ fontSize: '0.72rem', py: 0.3, textTransform: 'none', fontWeight: 700, ml: 'auto' }}>
                                                Stop
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            ))}

            {/* Action dialog */}
            <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3, m: 2 } }}>
                <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{dlgTitle}</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    {dialog?.type === 'stop' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            This plan will stop from the date you choose. You can undo it here before that date.
                        </Typography>
                    )}

                    <TextField
                        label={dialog?.type === 'resume' ? 'Resume from' : dialog?.type === 'stop' ? 'Stop from' : 'From date'}
                        type="date" fullWidth size="small"
                        value={form.date}
                        onChange={e => setForm(f => ({ ...f, date: e.target.value, endDate: (f.endDate && f.endDate <= e.target.value) ? addDaysISO(e.target.value, 2) : f.endDate }))}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: dialog?.type === 'stop' ? todayISO() : tomorrowISO() }}
                        helperText={dialog?.type === 'stop' ? 'Can be today — deliveries stop from the next day' : 'Earliest is tomorrow'}
                        sx={{ mt: 0.5 }}
                    />

                    {dialog?.type === 'qty' && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>New quantity</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Button variant="outlined" disabled={form.qty <= 1}
                                    onClick={() => setForm(f => ({ ...f, qty: Math.max(1, f.qty - 1) }))}
                                    sx={{ minWidth: 44, fontSize: '1.1rem', fontWeight: 800 }}>−</Button>
                                <Typography variant="h6" fontWeight={800} sx={{ minWidth: 32, textAlign: 'center' }}>{form.qty}</Typography>
                                <Button variant="outlined"
                                    onClick={() => setForm(f => ({ ...f, qty: f.qty + 1 }))}
                                    sx={{ minWidth: 44, fontSize: '1.1rem', fontWeight: 800 }}>+</Button>
                            </Box>
                        </Box>
                    )}

                    {dialog?.type === 'pause' && (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                select label="How long?" fullWidth size="small"
                                value={form.duration}
                                onChange={e => setForm(f => ({ ...f, duration: e.target.value, endDate: e.target.value === 'until' && !f.endDate ? addDaysISO(f.date, 2) : f.endDate }))}
                            >
                                {[1, 2, 3, 4, 5, 6, 7].map(n => (
                                    <MenuItem key={n} value={String(n)}>{n === 1 ? '1 day' : `${n} days`}</MenuItem>
                                ))}
                                <MenuItem value="until">Until a date</MenuItem>
                                <MenuItem value="forever">Indefinitely</MenuItem>
                            </TextField>
                            {form.duration !== 'until' && form.duration !== 'forever' && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Starts back on {fmtDay(addDaysISO(form.date, Number(form.duration)))}
                                </Typography>
                            )}
                            {form.duration === 'until' && (
                                <TextField
                                    label="Resume on" type="date" fullWidth size="small"
                                    value={form.endDate}
                                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: addDaysISO(form.date, 1) }}
                                    sx={{ mt: 1.5 }}
                                />
                            )}
                            {form.duration === 'forever' && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Paused until you resume it yourself.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submit} disabled={busy}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        {busy ? 'Saving…' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="info" onClose={() => setSnack('')} sx={{ width: '100%' }}>{snack}</Alert>
            </Snackbar>
        </Box>
    );
};

const HomeTab = ({ vendors, vendorBalances, balancesLoading, onShopVendor }) => {
    const [subTab, setSubTab] = useState(0); // 0=Orders 1=Bills 2=Payments
    const [items, setItems] = useState({ orders: [], bills: [], payments: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [closedExpanded, setClosedExpanded] = useState(false);
    // Keys are `${refType}-${refId}` using the backend's vocabulary: 'order-123' / 'bill-456'
    const [notifying, setNotifying] = useState(null);
    const [notified, setNotified] = useState({});     // { 'order-123': true }
    const [upiCopied, setUpiCopied] = useState(null); // key of item whose UPI was just copied
    const [claimMsg, setClaimMsg] = useState('');

    const handleNotify = async (refType, refId, vendor, amount) => {
        const key = `${refType}-${refId}`;
        setNotifying(key);
        try {
            await businessAPI.createPaymentClaim(vendor.f_id, {
                f_reference_type: refType,
                f_reference_id: refId ?? null,
                f_amount: parseFloat(amount) || 0,
                f_payment_method: 'UPI',
            });
            setNotified(prev => ({ ...prev, [key]: true }));
        } catch {
            setClaimMsg('Could not notify vendor. Please try again.');
        } finally {
            setNotifying(null);
        }
    };

    const totalDue    = Object.values(vendorBalances).reduce((s, b) => s + (b?.outstanding_balance || 0), 0);
    const totalBills  = Object.values(vendorBalances).reduce((s, b) => s + (b?.bills_outstanding || 0), 0);
    const totalOrders = Object.values(vendorBalances).reduce((s, b) => s + (b?.orders_outstanding || 0), 0);
    const vendorsWithDue = vendors.filter(v => (vendorBalances[v.f_id]?.outstanding_balance || 0) > 0);

    useEffect(() => {
        if (vendors.length === 0) return;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await businessAPI.getMyActivity(30);
                const d = res.data;
                setItems({
                    orders:   d.orders   || [],
                    bills:    d.bills    || [],
                    payments: d.payments || [],
                });
            } catch {
                setError('Failed to load activity');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [vendors]);

    if (vendors.length === 0) return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <VendorIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="body1" color="text.secondary">No vendors registered</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Contact your vendor to get added</Typography>
        </Box>
    );

    return (
        <Box>
            {/* Brand hero — outstanding at a glance (woven blue→orange, like the logo) */}
            {!balancesLoading && (
                <Box sx={{
                    position: 'relative', overflow: 'hidden',
                    borderRadius: 3, px: 2.25, py: 2.25, mb: 2, color: '#fff',
                    background: totalDue > 0
                        ? 'linear-gradient(120deg, #1E3A8A 0%, #2563EB 58%, #E8770C 165%)'
                        : 'linear-gradient(120deg, #1B5E20 0%, #2E7D32 100%)',
                    boxShadow: totalDue > 0
                        ? '0 8px 22px rgba(30,58,138,0.30)'
                        : '0 8px 22px rgba(46,125,50,0.24)',
                }}>
                    {/* soft corner glow */}
                    <Box sx={{ position: 'absolute', right: -22, top: -22, width: 110, height: 110, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.28), transparent 70%)' }} />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600, letterSpacing: 0.3 }}>
                        {totalDue > 0 ? 'Total Outstanding' : 'All Settled'}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ mt: 0.25, lineHeight: 1.05 }}>
                        {fmtAmt(totalDue)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 0.5 }}>
                        {totalDue > 0
                            ? `Owed across ${vendorsWithDue.length} vendor${vendorsWithDue.length !== 1 ? 's' : ''}`
                            : `No pending dues — you're all caught up 🎉`}
                    </Typography>
                </Box>
            )}

            {/* Dues detail — breakup + pay per vendor */}
            {!balancesLoading && totalDue > 0 && (
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 2 }}>
                    <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
                        {/* Bills / Orders breakup — tap to view */}
                        {(totalBills > 0 || totalOrders > 0) && (
                            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                {totalBills > 0 && (
                                    <Box onClick={() => { setSubTab(2); window.scrollTo(0, 0); }}
                                        sx={{ flex: 1, p: 1.25, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Bills</Typography>
                                        <Typography variant="body2" fontWeight="bold">{fmtAmt(totalBills)}</Typography>
                                        <Typography variant="caption" color="secondary.main" fontWeight={700}>View →</Typography>
                                    </Box>
                                )}
                                {totalOrders > 0 && (
                                    <Box onClick={() => { setSubTab(0); window.scrollTo(0, 0); }}
                                        sx={{ flex: 1, p: 1.25, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Orders</Typography>
                                        <Typography variant="body2" fontWeight="bold">{fmtAmt(totalOrders)}</Typography>
                                        <Typography variant="caption" color="secondary.main" fontWeight={700}>View →</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Per-vendor pay */}
                        {vendorsWithDue.map(v => {
                            const bal = vendorBalances[v.f_id]?.outstanding_balance || 0;
                            return (
                                <Box key={v.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.85, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{v.f_name}</Typography>
                                    <Typography variant="body2" fontWeight="bold" color="error.main">{fmtAmt(bal)}</Typography>
                                    {v.f_upi_id ? (
                                        <IconButton size="small" color={upiCopied === `dues-${v.f_id}` ? 'success' : 'default'}
                                            title="Copy UPI ID"
                                            onClick={() => {
                                                navigator.clipboard.writeText(v.f_upi_id);
                                                setUpiCopied(`dues-${v.f_id}`);
                                                setTimeout(() => setUpiCopied(null), 2500);
                                            }}
                                            sx={{ border: '1px solid', borderColor: 'divider', p: 0.5, flexShrink: 0 }}>
                                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    ) : (
                                        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>Contact vendor</Typography>
                                    )}
                                </Box>
                            );
                        })}

                        {/* Safety note */}
                        <Box sx={{ mt: 1.25, p: 1, bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.light', borderRadius: 1.5 }}>
                            <Typography variant="caption" color="warning.dark" sx={{ lineHeight: 1.4 }}>
                                ⚠️ <strong>Pay only after delivery.</strong> UPI payments happen outside Vendora and are not recorded here.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}


            <Snackbar
                open={!!claimMsg}
                autoHideDuration={4000}
                onClose={() => setClaimMsg('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setClaimMsg('')} sx={{ width: '100%' }}>
                    {claimMsg}
                </Alert>
            </Snackbar>

            {/* Segmented control */}
            <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, mb: 2, bgcolor: 'grey.100', borderRadius: 2.5 }}>
                {['Orders', 'Subscriptions', 'Monthly Bills', 'Payments'].map((label, i) => (
                    <Box key={label} onClick={() => { setSubTab(i); window.scrollTo(0, 0); }}
                        sx={{
                            flex: 1, textAlign: 'center', py: 0.85, borderRadius: 2, cursor: 'pointer',
                            fontSize: '0.8rem', fontWeight: 700, userSelect: 'none', transition: 'all 0.18s',
                            color: subTab === i ? '#fff' : 'text.secondary',
                            bgcolor: subTab === i ? 'secondary.main' : 'transparent',
                            boxShadow: subTab === i ? '0 2px 6px rgba(232,119,12,0.35)' : 'none',
                        }}>
                        {label}
                    </Box>
                ))}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {subTab === 1 ? (
                <CustomerPlansSection vendors={vendors} onShopVendor={onShopVendor} />
            ) : loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} /></Box>
            ) : (
                <>
                    {/* Orders — split into Active (in progress, or delivered but still due)
                        and Closed (cancelled, or delivered with payment settled) */}
                    {subTab === 0 && (() => {
                        if (items.orders.length === 0) {
                            return <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No orders yet</Typography>;
                        }
                        const isActiveOrder = (o) =>
                            o.f_status !== 'cancelled' &&
                            (o.f_status !== 'delivered' || o.f_payment_status === 'due');
                        const active = items.orders.filter(isActiveOrder);
                        const closed = items.orders.filter(o => !isActiveOrder(o));
                        const renderOrder = (o, idx) => {
                            const vendor = vendors.find(x => x.f_id === o.business_id);
                            const canPay = o.f_status === 'delivered' && o.f_payment_status === 'due' && !!vendor?.f_upi_id;
                            return (
                            <Card key={`o-${o.f_id}-${idx}`} variant="outlined"
                                sx={{ mb: 1.25, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: STATUS_ACCENT[o.f_status] || 'divider' }}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.25 } }}>
                                    {/* Vendor — highlighted, with quick contact */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <BusinessLogo src={vendor?.f_logo_url} name={o.vendor_name} size={26} radius="7px" />
                                        <Typography variant="body2" fontWeight={800} noWrap sx={{ flex: 1, minWidth: 0 }}>{o.vendor_name}</Typography>
                                        {vendor?.f_phone && (
                                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                <IconButton size="small" component="a" href={`tel:${vendor.f_phone}`}
                                                    sx={{ color: '#2563EB', border: '1px solid', borderColor: 'divider', p: 0.5 }}>
                                                    <PhoneIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                                <IconButton size="small" component="a" href={waLink(vendor.f_phone)} target="_blank" rel="noopener"
                                                    sx={{ color: '#25D366', border: '1px solid', borderColor: 'divider', p: 0.5 }}>
                                                    <WhatsAppIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle2" fontWeight={800}>{fmtAmt(o.f_total_amount)}</Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                {o.f_inserted_at ? new Date(o.f_inserted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
                                            <Chip label={o.f_status} size="small" color={STATUS_COLORS_MAP[o.f_status] || 'default'} sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }} />
                                            <Chip label={o.f_payment_status?.replace('_', ' ')} size="small" color={PAYMENT_COLORS_MAP[o.f_payment_status] || 'default'} variant="outlined" sx={{ height: 20, fontSize: '0.62rem' }} />
                                        </Box>
                                    </Box>
                                    {o.items?.length > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }} noWrap>
                                            {o.items.map(i => i.product_name).join(', ')}
                                        </Typography>
                                    )}
                                    {o.f_status === 'created' && (
                                        <Typography variant="caption" color="text.secondary"
                                            sx={{ display: 'block', mt: 0.75, p: 0.75, borderRadius: 1, bgcolor: 'action.hover', lineHeight: 1.4 }}>
                                            To change or cancel this order, please contact the vendor{vendor?.f_phone ? ' using the call or WhatsApp buttons above' : ''}.
                                        </Typography>
                                    )}
                                    <Button size="small" variant="text"
                                        onClick={() => openShareUrl(() => orderAPI.getOrderShareUrl(o.business_id, o.f_id))}
                                        sx={{ p: 0, minWidth: 0, fontSize: '0.72rem', textTransform: 'none', color: 'text.secondary', fontWeight: 600, mt: 0.75 }}>
                                        View Order →
                                    </Button>
                                    {canPay && (
                                        <Box sx={{ mt: 1, p: 1.25, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="caption" color="text.secondary">Pay via UPI</Typography>
                                                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>
                                                        {vendor.f_upi_id}
                                                    </Typography>
                                                </Box>
                                                <IconButton size="small" color={upiCopied === `order-${o.f_id}` ? 'success' : 'default'}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(vendor.f_upi_id);
                                                        setUpiCopied(`order-${o.f_id}`);
                                                        setTimeout(() => setUpiCopied(null), 2500);
                                                    }}>
                                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                            {notified[`order-${o.f_id}`] ? (
                                                <Typography variant="caption" color="success.main" fontWeight={700} sx={{ display: 'block', mt: 0.75 }}>
                                                    ✓ Vendor notified
                                                </Typography>
                                            ) : (
                                                <Button size="small" fullWidth variant="outlined" color="primary"
                                                    disabled={notifying === `order-${o.f_id}`}
                                                    onClick={() => handleNotify('order', o.f_id, vendor, o.f_total_amount)}
                                                    sx={{ mt: 0.75, fontSize: '0.7rem', textTransform: 'none', py: 0.5, fontWeight: 700 }}>
                                                    {notifying === `order-${o.f_id}` ? 'Notifying…' : 'I paid — Notify Vendor'}
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                            );
                        };
                        return (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">Active Orders</Typography>
                                    {active.length > 0 && (
                                        <Chip label={active.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.6rem' }} />
                                    )}
                                </Box>
                                {active.length === 0
                                    ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No active orders.</Typography>
                                    : active.map(renderOrder)}
                                {closed.length > 0 && (
                                    <>
                                        <Box onClick={() => setClosedExpanded(v => !v)}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, mt: 2, cursor: 'pointer', userSelect: 'none' }}>
                                            <Typography variant="subtitle2" fontWeight="bold">Closed Orders</Typography>
                                            <Chip label={closed.length} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                                <Typography variant="caption" fontWeight={600}>{closedExpanded ? 'Hide' : 'Show'}</Typography>
                                                <ChevronRightIcon sx={{ fontSize: 18, transition: 'transform 0.2s', transform: closedExpanded ? 'rotate(90deg)' : 'none' }} />
                                            </Box>
                                        </Box>
                                        {closedExpanded && closed.map(renderOrder)}
                                    </>
                                )}
                            </>
                        );
                    })()}

                    {/* Monthly Bills */}
                    {subTab === 2 && (items.bills.length === 0
                        ? <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No bills yet</Typography>
                        : items.bills.map((inv, idx) => {
                            const billVendor = vendors.find(v => v.f_id === inv.business_id);
                            const billDue = parseFloat(inv.balance_due) || 0;
                            const canPayBill = billDue > 0 && !!billVendor?.f_upi_id;
                            return (
                            <Card key={`b-${inv.f_id}-${idx}`} variant="outlined" sx={{ mb: 1.25, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: BILL_ACCENT[inv.payment_status] || 'divider' }}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">{inv.invoice_period_display}</Typography>
                                            <Typography variant="caption" color="text.secondary">{inv.f_invoice_number}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            <Chip label={inv.vendor_name} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', borderColor: 'primary.light', color: 'primary.main' }} />
                                            <Chip label={inv.payment_status} size="small" color={BILL_COLORS_MAP[inv.payment_status] || 'default'} sx={{ height: 18, fontSize: '0.6rem' }} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Total: <strong>{fmtAmt(inv.f_total_amount)}</strong></Typography>
                                        {billDue > 0 && (
                                            <Typography variant="caption" color="error.main">Due: <strong>{fmtAmt(billDue)}</strong></Typography>
                                        )}
                                    </Box>
                                    <Button size="small" variant="text"
                                        onClick={() => openShareUrl(() => invoiceAPI.getInvoiceShareUrl(inv.business_id, inv.f_id))}
                                        sx={{ p: 0, fontSize: '0.72rem', textTransform: 'none', color: 'primary.main', mt: 0.5 }}>
                                        View Bill →
                                    </Button>
                                    {canPayBill && (
                                        <Box sx={{ mt: 1, p: 1.25, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="caption" color="text.secondary">Pay via UPI</Typography>
                                                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>
                                                        {billVendor.f_upi_id}
                                                    </Typography>
                                                </Box>
                                                <IconButton size="small" color={upiCopied === `bill-${inv.f_id}` ? 'success' : 'default'}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(billVendor.f_upi_id);
                                                        setUpiCopied(`bill-${inv.f_id}`);
                                                        setTimeout(() => setUpiCopied(null), 2500);
                                                    }}>
                                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                            {notified[`bill-${inv.f_id}`] ? (
                                                <Typography variant="caption" color="success.main" fontWeight={700} sx={{ display: 'block', mt: 0.75 }}>
                                                    ✓ Vendor notified
                                                </Typography>
                                            ) : (
                                                <Button size="small" fullWidth variant="outlined" color="primary"
                                                    disabled={notifying === `bill-${inv.f_id}`}
                                                    onClick={() => handleNotify('bill', inv.f_id, billVendor, billDue)}
                                                    sx={{ mt: 0.75, fontSize: '0.7rem', textTransform: 'none', py: 0.5, fontWeight: 700 }}>
                                                    {notifying === `bill-${inv.f_id}` ? 'Notifying…' : 'I paid — Notify Vendor'}
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                            );
                        })
                    )}

                    {/* Payments */}
                    {subTab === 3 && (items.payments.length === 0
                        ? <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No payments yet</Typography>
                        : items.payments.map((p, idx) => (
                            <Card key={`p-${p.f_id}-${idx}`} variant="outlined" sx={{ mb: 1.25, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: '#2E7D32' }}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold" color="success.main">{fmtAmt(p.f_amount)}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {p.f_time ? new Date(p.f_time).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Chip label={p.vendor_name} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', borderColor: 'primary.light', color: 'primary.main' }} />
                                            <Chip label={p.f_payment_method} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                                        </Box>
                                    </Box>
                                    {p.f_notes && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{p.f_notes}</Typography>}
                                </CardContent>
                            </Card>
                        ))
                    )}
                </>
            )}
        </Box>
    );
};

// ── Profile Tab ───────────────────────────────────────────────────────────────

const ProfileTab = ({ user, themeMode, onToggleTheme, onLogout, onSwitchToVendor, hasVendorMode, onManageAddress }) => {
    return (
        <Box>
            {/* Account card */}
            <Card elevation={1} sx={{ borderRadius: 2, mb: 2 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, fontSize: '1.25rem' }}>
                        {(user?.f_name || user?.f_mobile || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="body1" fontWeight="bold">{user?.f_name || 'My Account'}</Typography>
                        <Typography variant="body2" color="text.secondary">{user?.f_mobile}</Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Settings */}
            <Card elevation={1} sx={{ borderRadius: 2 }}>
                <MenuItem onClick={onManageAddress} sx={{ py: 1.5 }}>
                    <LocationOnIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2">My Addresses</Typography>
                    <ChevronRightIcon sx={{ ml: 'auto', fontSize: 20, color: 'text.disabled' }} />
                </MenuItem>
                <Divider />
                {onToggleTheme && (
                    <>
                        <MenuItem onClick={onToggleTheme} sx={{ py: 1.5 }}>
                            {themeMode === 'dark' ? <LightModeIcon sx={{ mr: 1.5, fontSize: 20 }} /> : <DarkModeIcon sx={{ mr: 1.5, fontSize: 20 }} />}
                            <Typography variant="body2">{themeMode === 'dark' ? 'Light mode' : 'Dark mode'}</Typography>
                        </MenuItem>
                        <Divider />
                    </>
                )}
                {hasVendorMode && onSwitchToVendor && (
                    <>
                        <MenuItem onClick={onSwitchToVendor} sx={{ py: 1.5 }}>
                            <VendorIcon sx={{ mr: 1.5, fontSize: 20 }} />
                            <Typography variant="body2">Switch to Vendor View</Typography>
                        </MenuItem>
                        <Divider />
                    </>
                )}
                <MenuItem onClick={onLogout} sx={{ py: 1.5, color: 'error.main' }}>
                    <ExitToApp sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" color="error.main">Logout</Typography>
                </MenuItem>
            </Card>
        </Box>
    );
};

// ── Main CustomerDashboard ────────────────────────────────────────────────────

const TABS = [
    { label: 'Home', icon: <HomeIcon /> },
    { label: 'Shop', icon: <ShopIcon /> },
    { label: 'Profile', icon: <AccountCircle /> },
];

const CustomerDashboard = ({ user, vendors: vendorsProp, onSwitchToVendor, themeMode, onToggleTheme, onLogout, initialBusinessId }) => {
    const [activeTab, setActiveTab] = useState(() => {
        const t = parseInt(localStorage.getItem('vendora.customerTab'), 10);
        return (t === 0 || t === 1 || t === 2) ? t : 1; // default: Shop
    });
    const [barsVisible, setBarsVisible] = useState(true);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [vendorBalances, setVendorBalances] = useState({});
    const [balancesLoading, setBalancesLoading] = useState(false);
    // Drill-down states
    const [shopVendor, setShopVendor] = useState(null);        // vendor object for ordering
    const [shopVendorMode, setShopVendorMode] = useState('order'); // initial tab for NewOrderView
    const [showManageAddress, setShowManageAddress] = useState(false);
    const [pendingCart, setPendingCart] = useState(null); // { businessId, itemCount, vendorName }
    // Deep-link: open a specific business when arriving from its public store
    const [pendingBusinessId, setPendingBusinessId] = useState(initialBusinessId || null);

    // Once vendors have loaded, open the requested business (registered → order flow,
    // otherwise its public store renders inline via the shopVendor branch).
    useEffect(() => {
        if (pendingBusinessId == null || loading) return;
        const v = vendors.find(x => x.f_id === pendingBusinessId) || { f_id: pendingBusinessId };
        setShopVendor(v);
        setPendingBusinessId(null);
    }, [pendingBusinessId, loading, vendors]);

    // Persist cart banner — read localStorage and find vendor name
    const refreshPendingCart = useCallback(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('vendora.customerCart') || 'null');
            if (saved?.businessId && saved?.items) {
                const count = Object.values(saved.items).reduce((s, q) => s + q, 0);
                if (count > 0) {
                    const vendor = vendors.find(v => v.f_id === saved.businessId);
                    setPendingCart({ businessId: saved.businessId, itemCount: count, vendorName: vendor?.f_name || '…' });
                    return;
                }
            }
        } catch { }
        setPendingCart(null);
    }, [vendors]);

    useEffect(() => { refreshPendingCart(); }, [refreshPendingCart]);
    useEffect(() => { if (!shopVendor) refreshPendingCart(); }, [shopVendor, refreshPendingCart]);

    // Scroll-hide behaviour
    useEffect(() => {
        let lastY = window.scrollY;
        const handleScroll = () => {
            const y = window.scrollY;
            if (y < 50) { setBarsVisible(true); lastY = y; return; }
            if (y > lastY + 24) { setBarsVisible(false); lastY = y; return; }
            if (y < lastY - 48) { setBarsVisible(true); lastY = y; return; }
            lastY = y;
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Load vendor list
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await businessAPI.listCustomerVendors();
                setVendors(res.data || []);
            } catch {
                setError('Failed to load vendors');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // Load balances for all vendors
    useEffect(() => {
        if (vendors.length === 0) return;
        const loadBalances = async () => {
            setBalancesLoading(true);
            const results = await Promise.allSettled(
                vendors.map(v => businessAPI.getMyBalance(v.f_id))
            );
            const map = {};
            results.forEach((r, i) => {
                if (r.status === 'fulfilled') map[vendors[i].f_id] = r.value.data;
            });
            setVendorBalances(map);
            setBalancesLoading(false);
        };
        loadBalances();
    }, [vendors]);

    const handleTabChange = (_, v) => {
        setActiveTab(v);
        try { localStorage.setItem('vendora.customerTab', String(v)); } catch { /* ignore */ }
        setBarsVisible(true);
        window.scrollTo(0, 0);
    };

    // Drill-down: manage addresses
    if (showManageAddress) {
        return <ManageAddressPage onBack={() => setShowManageAddress(false)} />;
    }

    // Drill-down: shop from a vendor
    if (shopVendor) {
        const isRegistered = vendors.some(v => v.f_id === shopVendor.f_id);

        // Non-registered vendor — render public store inline (no redirect)
        if (!isRegistered) {
            return <PublicStorePage businessId={shopVendor.f_id} onBack={() => setShopVendor(null)} />;
        }

        // Registered vendor — full order placement flow
        return (
            <>
                <AppBar position="fixed" color="default" elevation={1} sx={{
                    top: 0, paddingTop: 'env(safe-area-inset-top, 0px)',
                }}>
                    <Toolbar variant="dense" sx={{ minHeight: 48 }}>
                        <IconButton edge="start" onClick={() => setShopVendor(null)} sx={{ mr: 1 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ flexGrow: 1 }}>
                            {shopVendor.f_name}
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Box sx={{
                    mt: 'calc(48px + env(safe-area-inset-top, 0px))',
                    pb: 2, px: { xs: 1.5, sm: 3 }, pt: 1.5,
                    maxWidth: 720, mx: 'auto',
                }}>
                    <NewOrderView
                        businessId={shopVendor.f_id}
                        user={user}
                        onBack={() => setShopVendor(null)}
                        onOrderCreated={() => setShopVendor(null)}
                        hideHeader
                        startMode={shopVendorMode}
                    />
                </Box>
            </>
        );
    }

    // Keep the header, content, and bottom nav aligned to the same page column.
    const contentMaxWidth = activeTab === 1 ? 1160 : 640;

    return (
        <>
            {/* ── AppBar ── */}
            <AppBar position="fixed" color="default" elevation={1} sx={{
                top: 0,
                paddingTop: 'env(safe-area-inset-top, 0px)',
                transform: barsVisible ? 'translateY(0)' : 'translateY(-100%)',
                transition: 'transform 0.25s ease',
            }}>
                <Toolbar variant="dense" disableGutters sx={{ minHeight: 48 }}>
                    <Box sx={{
                        width: '100%', maxWidth: contentMaxWidth, mx: 'auto',
                        px: { xs: 1.5, sm: 3 }, display: 'flex', alignItems: 'center',
                    }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                            {TABS[activeTab].label}
                        </Typography>
                        {vendorsProp && vendorsProp.length > 0 && onSwitchToVendor && (
                            <IconButton size="small" onClick={onSwitchToVendor} title="Switch to Vendor View">
                                <VendorIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* ── Main content ── */}
            <Box sx={{
                mt: 'calc(48px + env(safe-area-inset-top, 0px))',
                pb: pendingCart
                    ? 'calc(128px + env(safe-area-inset-bottom, 0px))'
                    : 'calc(64px + env(safe-area-inset-bottom, 0px) + 8px)',
                px: { xs: 1.5, sm: 3 },
                pt: 1.5,
                minHeight: 'calc(100dvh - 48px - env(safe-area-inset-top, 0px))',
                // Shop uses the wide grid (like /discover); Home/Profile stay a tidy column.
                maxWidth: contentMaxWidth,
                mx: 'auto',
                width: '100%',
            }}>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                ) : (
                    <>
                        {activeTab === 0 && (
                            <HomeTab
                                vendors={vendors}
                                vendorBalances={vendorBalances}
                                balancesLoading={balancesLoading}
                                onShopVendor={(vendor, mode = 'order') => { setShopVendorMode(mode); setShopVendor(vendor); }}
                            />
                        )}
                        {activeTab === 1 && (
                            <ShopTab
                                vendors={vendors}
                                onSelectVendor={setShopVendor}
                                onManageAddress={() => setShowManageAddress(true)}
                            />
                        )}
                        {activeTab === 2 && (
                            <ProfileTab
                                user={user}
                                themeMode={themeMode}
                                onToggleTheme={onToggleTheme}
                                onLogout={onLogout}
                                onSwitchToVendor={onSwitchToVendor}
                                hasVendorMode={vendorsProp && vendorsProp.length > 0}
                                onManageAddress={() => setShowManageAddress(true)}
                            />
                        )}
                    </>
                )}
            </Box>

            {/* ── Persistent cart banner ── */}
            {pendingCart && (
                <Paper elevation={6} sx={{
                    position: 'fixed',
                    bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
                    left: 0, right: 0, maxWidth: 640, mx: 'auto',
                    px: 1.5, py: 1, zIndex: 1299,
                    display: 'flex', alignItems: 'center', gap: 1,
                    borderRadius: '12px 12px 0 0',
                    borderTop: '1px solid', borderColor: 'divider',
                }}>
                    <CartIcon color="primary" fontSize="small" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                            {pendingCart.itemCount} item{pendingCart.itemCount > 1 ? 's' : ''} · {pendingCart.vendorName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Cart saved — tap to resume</Typography>
                    </Box>
                    <Button variant="contained" size="small"
                        onClick={() => {
                            setShopVendorMode('order');
                            setShopVendor(vendors.find(v => v.f_id === pendingCart.businessId) || { f_id: pendingCart.businessId });
                        }}
                        sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0 }}>
                        Resume
                    </Button>
                    <IconButton size="small" onClick={() => { localStorage.removeItem('vendora.customerCart'); setPendingCart(null); }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Paper>
            )}

            {/* ── Bottom Navigation ── */}
            <Box sx={{
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300,
                boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
                borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                opacity: barsVisible ? 1 : 0,
                visibility: barsVisible ? 'visible' : 'hidden',
                pointerEvents: barsVisible ? 'auto' : 'none',
                transition: 'opacity 0.25s ease, visibility 0.25s ease',
            }}>
                <BottomNavigation value={activeTab} onChange={handleTabChange} showLabels sx={{ height: 64, width: '100%', maxWidth: 640, mx: 'auto' }}>
                    {TABS.map(t => (
                        <BottomNavigationAction key={t.label} label={t.label} icon={t.icon} sx={{ minWidth: 0, flex: 1 }} />
                    ))}
                </BottomNavigation>
            </Box>
        </>
    );
};

export default CustomerDashboard;
